import classNames from 'classnames';
import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import customerLogo from '../../assets/customerLogo.png';
import { BrandingIconCard, ResponsiveImage, CurrencySymbol, TransactionModelBadge } from '../../components';
import IconCollection from '../../components/IconCollection/IconCollection';
import useCategoryConfigMap from '../../hooks/useCategoryConfigMap';
import { configForListing } from '../../util/data';
import { formatListingPriceAmount } from '../../util/currency';
import { createResourceLocatorString } from '../../util/routes';
import { mergeFromIntoIntent, POST_AUTH_ACTIONS } from '../../util/postAuthIntent';
import { DEAL_LISTING_TYPE, PRODUCT_LISTING_TYPE, SERVICE_LISTING_TYPE } from '../../util/types';
import { createSlug } from '../../util/urlHelpers';
import { onUpdateAdClick } from '../../util/api';
import css from './AlgoliaSearchServiceCard.module.css';

const ROOT_URL = process.env.REACT_APP_MARKETPLACE_ROOT_URL;

// Inline map-pin — Lovable services card prefixes the place with `MapPin`.
const MapPinIcon = props => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

// Inline filled star — Lovable services card uses `Star fill-primary text-primary`.
const StarFilledIcon = props => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinejoin="round"
    aria-hidden="true"
    {...props}
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

/**
 * AlgoliaSearchServiceCard
 *
 * Restyle reference: lovable_template/src/routes/services.tsx
 *
 *   <article rounded-3xl ring-1>
 *     <div aspect-[5/4]><img group-hover:scale-110 /></div>
 *     <div p-4>
 *       <h3 text-base font-bold>{title}</h3>
 *       <div mt-0.5 text-xs ink-soft>{place}</div>
 *       <div mt-3 flex items-center justify-between>
 *         <div>
 *           <div text-[11px] line-through>{old}</div>
 *           <div text-xl font-extrabold>{price}</div>
 *         </div>
 *         <Button size-sm rounded-full>Book</Button>
 *       </div>
 *     </div>
 *   </article>
 *
 * now-app preserves the heart-favorite button and WhatsApp row (conditional)
 * since those drive real customer functionality outside the Lovable mock.
 */
const AlgoliaSearchServiceCard = ({ intl, ...props }) => {
  const {
    hit,
    currentUser,
    toggleFavorite,
    history,
    routeConfiguration: routes,
    isBookingLink,
    surface = 'light',
  } = props;
  const { title, price, images = [], publicData = {}, author = {} } = hit || {};
  const { currency, amount } = price || {};
  const { displayName: authorName, publicData: authorPublicData } = author || {};
  const {
    listingType,
    original_price,
    totalReviews = 0,
    averageRating = 0,
  } = publicData || {};

  const reviewCount = Number(totalReviews) || 0;
  const ratingValue = Number(averageRating) || 0;

  // Joined by category slug at render time rather than read off the hit, so an
  // admin config change shows up on the next load with no listing rewrite and
  // no Algolia re-index. Null for legacy-taxonomy listings — badge just hides.
  const categoryConfigMap = useCategoryConfigMap();
  const listingConfig = configForListing(categoryConfigMap, publicData);
  const isNegotiatedModel = listingConfig?.transactionModel === 'negotiated';

  const businessName =
    authorPublicData?.business_name ||
    authorName ||
    [author?.firstName, author?.lastName].filter(Boolean).join(' ') ||
    '';
  const ownListing = hit?.author?.id === currentUser?.id?.uuid;

  const wishlistTypeKey =
    listingType === SERVICE_LISTING_TYPE
      ? 'wishlist_service_ids'
      : listingType === DEAL_LISTING_TYPE
        ? 'wishlist_deal_ids'
        : listingType === PRODUCT_LISTING_TYPE
          ? 'wishlist_product_ids'
          : null;

  const wishlist = currentUser?.attributes?.profile?.publicData?.[wishlistTypeKey] || [];
  const isFavorite = wishlist.includes(hit?.objectID);

  const id = hit?.objectID;
  const slug = createSlug(title);
  const isBooking = listingType === SERVICE_LISTING_TYPE;

  // Location — accept either an object ({ address, city }) or a plain string.
  const locationText =
    publicData?.location && typeof publicData.location === 'object'
      ? publicData.location.address || publicData.location.city || ''
      : typeof publicData?.location === 'string'
        ? publicData.location
        : '';

  const listingUrl = createResourceLocatorString(
    'ListingPage',
    routes,
    { id, slug },
    isBookingLink ? { bookingLink: true } : {}
  );

  const arabicLanguage =
    typeof window !== 'undefined' && localStorage.getItem('language') === 'Arabic';

  const handleFavoriteClick = () => {
    if (currentUser === null) {
      mergeFromIntoIntent(listingUrl, POST_AUTH_ACTIONS.BOOK);
      history.push(createResourceLocatorString('LoginPage', routes, {}, {}), { from: listingUrl });
    } else {
      toggleFavorite(hit?.objectID, listingType, currentUser, hit?.author?.id, title, images);
    }
  };

  const goToListing = async () => {
    if (hit?.isPromoted && hit?.adId) {
      await onUpdateAdClick({ adId: hit.adId });
    }
    history.push(listingUrl);
  };

  const listingUrlFull = ROOT_URL + listingUrl;
  const whatsappMessage = `Hello NOW Offers! , I’d like to book this service *${title}* for *${currency} ${(amount / 100)?.toFixed(2)}* by this merchant *${authorPublicData?.business_name}* on your platform. Please confirm availability. Thanks!
  ${listingUrlFull}`;

  const cardImageUrl = typeof images[0] === 'string' ? images[0] : null;

  return (
    <div className={classNames(css.servicesListBox, arabicLanguage && css.arabicDirection)}>
      <article
        className={classNames(css.serviceCard, surface === 'dark' && css.serviceCardOnDark)}
      >
        <div className={css.cardContent} onClick={goToListing}>
          <div className={css.imageWrapper}>
            {cardImageUrl ? (
              <ResponsiveImage alt={title} localImageSrc={cardImageUrl} variants={['custom-image']} />
            ) : images[0] ? (
              <ResponsiveImage
                alt={title}
                image={images[0]}
                variants={['listing-card', 'listing-card-2x']}
              />
            ) : (
              <ResponsiveImage alt={title} localImageSrc={customerLogo} variants={['custom-image']} />
            )}
            <TransactionModelBadge config={listingConfig} className={css.transactionBadge} />
          </div>

          <div className={css.cardBody}>
            <h3 className={css.title}>{title}</h3>

            {businessName || locationText ? (
              <div
                className={css.metaText}
                title={[businessName, locationText].filter(Boolean).join(' · ')}
              >
                <MapPinIcon className={css.metaIcon} />
                <span className={css.metaTextInner}>
                  {businessName ? (
                    <span className={css.businessName}>{businessName}</span>
                  ) : null}
                  {businessName && locationText ? (
                    <span className={css.metaSeparator} aria-hidden="true">
                      {' · '}
                    </span>
                  ) : null}
                  {locationText ? (
                    <span className={css.locationText}>{locationText}</span>
                  ) : null}
                </span>
              </div>
            ) : null}

            {/* Rating — filled yellow star + value + (count), Lovable services card. */}
            <div className={css.reviewDetails}>
              <StarFilledIcon className={css.starIcon} />
              <span className={css.ratingValue}>{ratingValue.toFixed(1)}</span>
              <span className={css.reviewCount}>({reviewCount})</span>
            </div>

            <div className={css.priceBookRow}>
              <div className={css.priceWrapper}>
                {isNegotiatedModel ? (
                  <span className={css.dealPrice}>
                    <FormattedMessage id="SearchPage.getAQuote" />
                  </span>
                ) : (
                  <>
                    {original_price ? (
                      <span className={css.actualPrice}>
                        <CurrencySymbol size={11} /> {formatListingPriceAmount(intl, original_price)}
                      </span>
                    ) : null}
                    <span className={css.dealPrice}>
                      <CurrencySymbol size={14} />{' '}
                      {amount ? formatListingPriceAmount(intl, amount / 100) : '0'}
                      {listingConfig?.transactionModel === 'hourly' ? (
                        <FormattedMessage id="ListingCard.perHourSuffix" />
                      ) : null}
                    </span>
                  </>
                )}
              </div>
              <button
                type="button"
                className={css.bookButton}
                onClick={e => {
                  e.stopPropagation();
                  goToListing();
                }}
              >
                <FormattedMessage
                  id={isNegotiatedModel ? 'SearchPage.getQuote' : isBooking ? 'SearchPage.book' : 'SearchPage.buy'}
                />
              </button>
            </div>

            {isBookingLink && isBooking ? (
              <div
                className={css.bookOnWhatsapp}
                onClick={e => {
                  e.stopPropagation();
                  if (typeof window !== 'undefined') {
                    window.open(
                      `https://wa.me/966539900466?text=${whatsappMessage}`,
                      '_blank'
                    );
                  }
                }}
              >
                <IconCollection name="whatsapp" />
                <span>
                  <FormattedMessage id="ServiceCard.bookOnWhatsapp" />
                </span>
              </div>
            ) : null}
          </div>
        </div>

        {!ownListing && (
          <span
            className={classNames(css.favoriteBtn, isFavorite && css.favoriteBtnActive)}
            role="button"
            tabIndex={0}
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              handleFavoriteClick();
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                handleFavoriteClick();
              }
            }}
            aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <BrandingIconCard type={isFavorite ? 'red-heart' : 'heart'} />
          </span>
        )}
      </article>
    </div>
  );
};

export default injectIntl(AlgoliaSearchServiceCard);
